import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CompareHelper, ObjectHelper, overrideDefaultRootColors } from '../../../../helpers';
import { Configuration } from '../models/config.model';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private static readonly STORAGE_KEY_WEB_API_BASE_URL: string = 'WebAPI_BaseUrl';
  public firstLoad = true;
  get configuration() {
    return this._configuration as Configuration;
  }
  set configuration(conf) {
    if (CompareHelper.isEmptyObject(this._configuration)) {
      this._configuration = {};
    }
    ObjectHelper.mergeDeepInto(this._configuration, conf);
  }
  // configuration: Configuration;

  public get isInitialized(): boolean {
    return this.configuration ? true : false;
  }
  _configuration;

  public isAppLike: boolean;

  constructor(private http: HttpClient) {}

  loadConfig(...configUrls: string[]): Promise<Configuration> {
    if (this.firstLoad) {
      configUrls.push('assets/config/global.config.json');
      configUrls.push('assets/config/system.config.json');
      this.firstLoad = false;
    }
    return new Promise((resolve) => {
      this._loadConfiguration(...configUrls).subscribe(
        (config) => {
          //this._configuration.DefaultRoute already set
          let webApiBaseURL: string;
          if (this.configuration?.WebApi?.AllowBaseURLChange) {
            webApiBaseURL = this.readWebApiBaseURLFromStorage();
          }
          if (!webApiBaseURL) {
            webApiBaseURL = this._configuration.WebApi.BaseURL;
          }
          this.setWebApiBaseURLCore(this._configuration, webApiBaseURL, true);

          overrideDefaultRootColors(this._configuration);
          resolve(this._configuration);
        },
        (err) => console.log(err),
        () => {},
      );
    });
  }

  private _loadConfiguration(...configUrls: string[]) {
    let queries = [];
    for (const element of configUrls) {
      queries.push(
        this.http.get(element).pipe(
          tap(
            (config: Configuration) => {
              this.configuration = config;
              // mergeDeep(this._configuration, appConfig);
            },
            (err) => console.log(err),
          ),
        ),
      );
    }
    return forkJoin(queries);
  }

  getConfiguration(): Configuration {
    return this.configuration as Configuration;
  }

  public setWebApiBaseURL(webApiBaseURL: string): void {
    if (this.configuration?.WebApi?.AllowBaseURLChange) {
      this.setWebApiBaseURLCore(this.configuration, webApiBaseURL, false);
    } else {
      console.error('Changing of BaseURL is not allowed!');
    }
  }

  private setWebApiBaseURLCore(config: Configuration, webApiBaseURL: string, doNotSaveInStorage = false): void {
    if (config) {
      const oldWebApiBaseURL: string = config.WebApi.BaseURL;
      this.setWebApiURLs(config, oldWebApiBaseURL, webApiBaseURL);
      config.WebApi.BaseURL = webApiBaseURL;

      if (!doNotSaveInStorage && oldWebApiBaseURL !== webApiBaseURL) {
        this.writeWebApiBaseURLToStorage(webApiBaseURL);
      }
    }
  }

  public getCleanWebApiBaseURL(url: string): string {
    if (url) {
      url = url.trim();

      if (url.endsWith('/')) {
        url = url.slice(0, url.length - 1);
      }
    }

    return url;
  }

  private setWebApiURLs(configContainer: any, oldWebApiBaseURL: string, newWebApiBaseURL: string): void {
    oldWebApiBaseURL = this.getCleanWebApiBaseURL(oldWebApiBaseURL);
    newWebApiBaseURL = this.getCleanWebApiBaseURL(newWebApiBaseURL);

    for (const settingKey of Object.keys(configContainer)) {
      const settingValue: any = configContainer[settingKey];

      if (typeof settingValue === 'string') {
        if (oldWebApiBaseURL && settingValue.startsWith(oldWebApiBaseURL)) {
          if (newWebApiBaseURL) {
            configContainer[settingKey] = settingValue.replace(oldWebApiBaseURL, newWebApiBaseURL); // replaces only first (= sufficient)
          } else {
            configContainer[settingKey] = settingValue.replace(oldWebApiBaseURL.concat('/'), ''); // replaces only first (= sufficient)
          }
        } else if (settingValue.startsWith('api') || settingValue.startsWith('Token')) {
          if (newWebApiBaseURL) {
            configContainer[settingKey] = `${newWebApiBaseURL}/${settingValue}`; // first init
          }
        }
      } else if (typeof settingValue === 'object' && settingValue !== null) {
        this.setWebApiURLs(settingValue, oldWebApiBaseURL, newWebApiBaseURL);
      }
    }
  }

  private readWebApiBaseURLFromStorage(): string {
    return (
      sessionStorage.getItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL) ||
      localStorage.getItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL)
    );
  }

  private writeWebApiBaseURLToStorage(webApiBaseURL: string): void {
    if (webApiBaseURL) {
      sessionStorage.setItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL, webApiBaseURL);
      localStorage.setItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL, webApiBaseURL);
    } else {
      sessionStorage.removeItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL);
      localStorage.removeItem(ConfigService.STORAGE_KEY_WEB_API_BASE_URL);
    }
  }

  public getWebApiBaseURL(): string {
    return this.getCleanWebApiBaseURL(this.configuration.WebApi.BaseURL);
  }
}
